const T = require('prop-types');
const React = require('react');

const { default: Typography } = require('@mui/material/Typography');
const { default: Button } = require('@mui/material/Button');
const { default: Styled } = require('styled-components');

const SimpleDialog = require('components/SimpleDialog');

const internals = {};

const DowntimeDialog = (props) => {

    const {
        isDowntimeMode,
        downtimeMessage,
        downtimeType,
        checkIsDowntimeMode,
        continueWithoutRetry,
        retryPendingRequest
    } = props;

    const { Actions } = internals;
    const isServiceUnavailable = downtimeType === 'service-unavailable';

    let parsedDowntimeMessage = downtimeMessage;

    if (downtimeMessage) {
        parsedDowntimeMessage = internals.localizeDowntimeMessage(downtimeMessage);
    }

    return (
        <SimpleDialog
            isOpen={isDowntimeMode}
            onConfirm={() => null}
            title={isServiceUnavailable ? 'Nearpeer is under heavy traffic' : "We'll Be Back Soon"}
            buttons={
                isServiceUnavailable ? (
                    <Actions>
                        <Button
                            variant='contained'
                            color='secondary'
                            onClick={continueWithoutRetry}
                            data-focus-outline='radius:40'
                        >
                            Continue
                        </Button>
                        <Button
                            variant='contained'
                            onClick={retryPendingRequest}
                            data-focus-outline='radius:40'
                        >
                            Try again
                        </Button>
                    </Actions>
                ) : (
                    <Button
                        variant='contained'
                        onClick={checkIsDowntimeMode}
                        data-focus-outline='radius:40'
                    >
                        Refresh
                    </Button>
                )
            }
            content={(
                <Typography>
                    {isServiceUnavailable && (parsedDowntimeMessage || 'Nearpeer is temporarily unavailable. Please try again shortly.')}
                    {!isServiceUnavailable && parsedDowntimeMessage?.split('\n').map((line, i) => {
                        // split out any "{hh:mm AM/PM}" tokens
                        const parts = line.split(/(\{\d{2}:\d{2} [AP]M\})/g);

                        return (
                            <React.Fragment key={i}>
                                {parts.map((part, j) => {

                                    // if this part is exactly "{hh:mm AM/PM}", bold the time inside
                                    const m = part.match(/^\{(\d{2}:\d{2} [AP]M)\}$/);
                                    if (m) {
                                        return <strong key={j}>{m[1]}</strong>;
                                    }

                                    // otherwise just render the text
                                    return part;
                                })}
                                <br />
                            </React.Fragment>
                        );
                    })}
                </Typography>
            )}
        />
    );
};

DowntimeDialog.propTypes = {
    isDowntimeMode: T.bool,
    downtimeMessage: T.string,
    downtimeType: T.oneOf(['maintenance', 'service-unavailable']),
    checkIsDowntimeMode: T.func,
    continueWithoutRetry: T.func,
    retryPendingRequest: T.func
};

module.exports = DowntimeDialog;

internals.localizeDowntimeMessage = (message) => {

    return message.replace(
        // match "{hh:mm AM/PM}" exactly
        /\{\s*(?:0[1-9]|1[0-2]):[0-5][0-9]\s*(?:AM|PM)\s*\}/g,
        (match) => `{${internals.parseEasternToLocal(match)}}`
    );
};

internals.Actions = Styled.div`
    display: flex;
    flex-flow: row nowrap;
    gap: 8px;
`;

/**
 * Given a "{hh:mm AM/PM}" or "hh:mm AM/PM" in America/New_York
 * time, convert it to a Date in the user's local zone and
 * return a localized "hh:mm AM/PM" string.
 */
internals.parseEasternToLocal = (estTimeStr) => {

    // 0) strip braces
    estTimeStr = estTimeStr.trim().replace(/^\{\s*(.+?)\s*\}$/, '$1');

    // 1) strict hh:mm AM/PM
    const match = estTimeStr.match(/^([0][1-9]|1[0-2]):([0-5][0-9])\s*([AP]M)$/);

    if (!match) {
        console.warn(`Bad format: "${estTimeStr}". Expected "hh:mm AM/PM".`);
        return estTimeStr;
    }

    const [, hh, mm, ap] = match;

    let h = parseInt(hh, 10) % 12;

    if (ap === 'PM') {
        h += 12;
    }

    const min = parseInt(mm, 10);

    const now = new Date();
    const todayOff = internals.getEasternOffset(now);
    // build a UTC-based timestamp by treating h/min as if UTC,
    // then ADD the Eastern offset so it becomes the true UTC ts:
    let ts = Date.UTC(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        h, min
    ) + todayOff * 60_000;

    let candidate = new Date(ts);

    // if that’s already behind local‐now, assume “tomorrow” in NY
    if (candidate < now) {
        const tomorrow = new Date(now.getTime() + 86_400_000);
        const tomOff = internals.getEasternOffset(tomorrow);

        ts = Date.UTC(
            tomorrow.getFullYear(),
            tomorrow.getMonth(),
            tomorrow.getDate(),
            h, min
        ) + tomOff * 60_000;

        candidate = new Date(ts);
    }

    // finally, let the browser print it for the user’s zone
    return candidate.toLocaleTimeString(undefined, {
        hour:   '2-digit',
        minute: '2-digit',
        hour12: true
    });
};

// helper: get offset (minutes) of NY on a given date
internals.getEasternOffset = (date) => {

    const dtf = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        timeZoneName: 'short'      // yields "EST" or "EDT"
    });
    const parts = dtf.formatToParts(date);
    const abbr = parts.find((p) => p.type === 'timeZoneName').value;
    return abbr === 'EDT' ? 4 * 60 : 5 * 60;
};
